import { ChangeEvent, useRef } from 'react'
import { t } from '@lingui/macro'

import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { keyframes } from '@mui/material/styles'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'

import { DictationState } from '../hooks/useDictation'

/** What Elara can read: photos (incl. the phone camera) and PDFs. */
export const ATTACHMENT_ACCEPT = 'image/*,application/pdf'

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
`

export type OmniboxInputToolsProps = {
  /** Undefined when the browser cannot record: no mic button. */
  dictation?: { state: DictationState; toggle: () => void }
  /** Undefined when the user cannot ask Elara (files only go to Elara): no paperclip. */
  onAttach?: (files: File[]) => void
}

/** Mic + paperclip shown at the end of the search field. */
export function OmniboxInputTools({ dictation, onAttach }: OmniboxInputToolsProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleFiles(event: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(event.target.files ?? [])
    event.target.value = '' // the same file can be picked again after removing it
    if (files.length > 0) onAttach?.(files)
  }

  const isRecording = dictation?.state === 'recording'
  const isTranscribing = dictation?.state === 'transcribing'
  const micLabel = isRecording ? t`Stop dictation` : t`Dictate`

  return (
    <>
      {onAttach && (
        <>
          <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} multiple hidden onChange={handleFiles} data-testid="omnibox-file-input" />
          <Tooltip title={t`Attach a photo or a PDF for Elara`}>
            <IconButton aria-label={t`Attach a photo or a PDF for Elara`} onClick={() => fileInputRef.current?.click()}>
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
      {dictation && (
        <Tooltip title={micLabel}>
          <span>
            <IconButton
              aria-label={micLabel}
              aria-pressed={isRecording}
              disabled={isTranscribing}
              onClick={dictation.toggle}
              sx={isRecording ? { color: 'common.white', backgroundColor: 'error.main', animation: `${pulse} 1.5s infinite`, '&:hover': { backgroundColor: 'error.dark' } } : undefined}
            >
              {isTranscribing ? <CircularProgress size={20} /> : isRecording ? <StopIcon /> : <MicIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  )
}
